/**
 * React Hook for RTDB-cached top scorers with real-time updates
 */

import { useState, useEffect, useCallback } from 'react';
import rtdbFootballService from '../rtdbFootballService';
import rtdbCache from '../rtdbCache';
import { CACHE_PATHS, getCacheKey, logCache } from '../rtdbConfig';

/**
 * Hook for fetching top scorers with RTDB caching and real-time updates
 * @param {Object} params - Query parameters
 * @param {string|number} params.league - League ID
 * @param {string} params.season - Season year
 * @param {boolean} params.enabled - Whether to fetch data
 * @param {boolean} params.realtime - Enable real-time updates
 * @returns {Object} Top scorers data, loading state, error, and refetch function
 */
export const useRTDBTopScorers = ({ 
  league, 
  season,
  enabled = true,
  realtime = true 
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cacheStatus, setCacheStatus] = useState('idle');

  const fetchTopScorers = useCallback(async () => {
    if (!enabled || !league || !season) return;

    try {
      setLoading(true);
      setError(null);
      setCacheStatus('fetching');
      
      logCache('info', 'useRTDBTopScorers', { league, season });
      
      // Use RTDB-enhanced service
      const response = await rtdbFootballService.getTopScorers(
        Number(league), 
        season
      );
      
      const scorers = response.response || [];
      setData(scorers);
      setCacheStatus('success');
    } catch (err) {
      setError(err.message || 'Failed to load top scorers');
      setCacheStatus('error');
      console.error('Failed to load top scorers:', err);
    } finally {
      setLoading(false);
    }
  }, [league, season, enabled]);

  // Set up real-time subscription
  useEffect(() => {
    if (!enabled || !realtime || !league || !season) return;

    let unsubscribe = () => {};
    
    // Build cache path for top scorers
    const cachePath = `${CACHE_PATHS.PLAYERS}/topscorers/${getCacheKey('league', league)}/${season}`;
    
    // Subscribe to real-time updates
    if (rtdbCache.isConnected()) {
      unsubscribe = rtdbCache.subscribe(cachePath, (data) => {
        if (data) {
          logCache('info', 'useRTDBTopScorers real-time update received');
          setData(data.response || []);
          setCacheStatus('realtime');
        }
      });
    }

    // Initial fetch
    fetchTopScorers();

    return () => {
      unsubscribe();
    };
  }, [league, season, enabled, realtime, fetchTopScorers]);

  return {
    data,
    loading,
    error,
    refetch: fetchTopScorers,
    cacheStatus,
    isEmpty: !loading && data.length === 0
  };
};

// Also export default for consistency
export default useRTDBTopScorers;